import { auth } from '../../config'
import { toast } from 'react-toastify'
import React from 'react'
import PropTypes from 'prop-types'
import withStyles from 'isomorphic-style-loader/withStyles'
import * as styles from './_HereMapsSearch.scss'
import fetch from 'node-fetch'

const HERE_MAPS_API_KEY = auth.hereMaps.apiKey
const BASE_AUTOCOMPLETE_SEARCH_URL = auth.hereMaps.baseUrl
const BASE_LOOKUP_URL = auth.hereMaps.lookupUrl
const DEFAULT_SUGGESTION_INDEX = -1

const debounce = (func, delay) => {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

class HereMapsSearch extends React.Component {
  static propTypes = {
    onPlacesChanged: PropTypes.func,
    markerAddress: PropTypes.string,
    setMarkerAddress: PropTypes.func.isRequired,
    containerRef: PropTypes.object
  };

  constructor (props) {
    super(props)
    this.state = {
      suggestions: [],
      activeIndex: DEFAULT_SUGGESTION_INDEX,
      justSelected: false
    }
    this.suggestionsRef = null
    this.inputRef = null

    this.setSuggestionsRef = element => {
      this.suggestionsRef = element
    }

    this.setInputRef = element => {
      this.inputRef = element
    }
    this.selectSuggestion = this.selectSuggestion.bind(this)
    this.fetchSuggestions = this.fetchSuggestions.bind(this)
    this.debouncedFetchSuggestions = debounce(this.fetchSuggestions, 300)
  }

  async fetchSuggestions (query) {
    const encodedQuery = `q=${encodeURIComponent(query)}`
    const countryQuery = `&in=countryCode:ZAF`
    const pagingQuery = `&limit=5`
    const apiCredentials = `&apiKey=${HERE_MAPS_API_KEY}`

    if (!query || query.length < 3) {
      this.setState({ suggestions: [], activeIndex: DEFAULT_SUGGESTION_INDEX })
      return
    }

    try {
      const endpoint = `${BASE_AUTOCOMPLETE_SEARCH_URL}?${encodedQuery}${countryQuery}${pagingQuery}${apiCredentials}`

      const response = await fetch(endpoint)
      const data = await response.json()
      const items = data.items || []
      this.setState({ suggestions: items, activeIndex: DEFAULT_SUGGESTION_INDEX })
    } catch (error) {
      this.setState({ suggestions: [] })
      toast.warn('Could not fetch location suggestions. Please try again.', {
        autoClose: 8000
      })
    }
  }

  handleKeyDown = (e) => {
    const { suggestions, activeIndex } = this.state
    if (!suggestions.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.setState(prev => ({
        activeIndex: Math.min(prev.activeIndex + 1, suggestions.length - 1)
      }))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.setState(prev => ({
        activeIndex: Math.max(prev.activeIndex - 1, 0)
      }))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        this.selectSuggestion(suggestions[activeIndex])
      }
    }
  };

  async selectSuggestion (suggestion) {
    const label = suggestion.title || ''
    this.props.setMarkerAddress(label.replace(/<[^>]*>/g, ''))
    this.setState({ suggestions: [], justSelected: true })

    try {
      const lookupUrl = `${BASE_LOOKUP_URL}?apiKey=${HERE_MAPS_API_KEY}&id=${encodeURIComponent(suggestion.id)}`

      const res = await fetch(lookupUrl)
      const placeDetails = await res.json()
      const item = placeDetails.items ? placeDetails.items[0] : placeDetails

      const placeResult = {
        name: item.title || '',
        formatted_address: (item.address && item.address.label) || label,
        place_id: item.id || '',
        geometry: {
          location: {
            lat: () => item.position.lat,
            lng: () => item.position.lng
          }
        }
      }
      if (this.props.onPlacesChanged) {
        this.props.onPlacesChanged([placeResult])
      }
    } catch (error) {
      toast.warn('Failed to retrieve location details. Please try again.', {
        autoClose: 8000
      })
    }
  }

  handleClickOutside = (event) => {
    if (this.suggestionsRef && !this.suggestionsRef.contains(event.target)) {
      this.setState({ suggestions: [] })
    }
  };

  componentDidMount () {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentDidUpdate (prevProps) {
    if (this.state.justSelected) {
      this.state.justSelected = true
      return
    }
    if (this.props.markerAddress !== prevProps.markerAddress) {
      this.debouncedFetchSuggestions(this.props.markerAddress)
    }
  }

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  render () {
    const { markerAddress } = this.props
    const { suggestions, activeIndex } = this.state

    return (
      <div className={styles['here-search-container']} ref={this.suggestionsRef}>
        <input
          ref={this.setInputRef}
          type='text'
          className={styles['searchbox']}
          placeholder='Street and town'
          value={markerAddress}
          onChange={(e) => {
            const value = e.target.value
            this.props.setMarkerAddress(value)
            this.setState({ justSelected: false })
          }}
          onKeyDown={this.handleKeyDown}
        />

        {suggestions.length > 0 && (
          <ul className={styles['suggestions-list']}>
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.id || index}
                className={`${styles['suggestion-item']} ${
                  index === activeIndex ? styles['active'] : ''
                }`}
                onClick={() => this.selectSuggestion(suggestion)}
              >
                {suggestion.title}
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }
}

export default (HereMapsSearch)
